import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LayoutDashboard,  Wheat, TrendingUp, Satellite, Bot, Sparkles, FileText, Languages, LogOut, Wallet, Shield, Sun, Moon, HelpCircle, RefreshCw, User, Users, MapPin, Zap, Calculator, FlaskConical, Droplets } from 'lucide-react';
import LoginView from './views/LoginView';
import { useGoogleLogin } from '@react-oauth/google';
import HelpGuideModal from './components/HelpGuideModal';
import Card from './components/ui/Card';
import NavBtn from './components/ui/NavBtn';
import IoTView from './views/IoTView';
import LeafDoctorView from './views/LeafDoctorView';
import CropRecommendView from './views/CropRecommendView';
import YieldPredictionView from './views/YieldPredictionView';
import SatelliteView from './views/SatelliteView';
import AIConsultantView from './views/AIConsultantView';
import NewAIView from './views/NewAIView';
import ControlAutomationView from './views/ControlAutomationView';
import AccountingView from './views/AccountingView';
import PesticideRecommenderView from './views/PesticideRecommenderView';
import WaterIrrigationView from './views/WaterIrrigationView';
import ExportReportModal from './components/ExportReportModal';
import TranslationService from './services/translationService';
import ApiService from './services/apiService';
import { FeatureGuidelines } from './components/Tooltips';
import { PaymentCheckout } from './components/PaymentCheckout';
import { FarmPlantSelector } from './components/FarmPlantSelector';
import AdminDashboardView from './views/AdminDashboardView';
import ProfileView from './views/ProfileView';
import SupportChatWidget from './components/SupportChatWidget';
import PaywallPopup from './components/PaywallPopup';
import GeoLogo from './brand/Logo_geo.svg';
import OnboardingWizard from './components/OnboardingWizard';
import Toast from './components/ui/Toast';



export default function SmartFarmApp() {
  const [toast, setToast] = useState({ message: '', type: 'success' });
  const showToast = (message, type = 'success') => setToast({ message, type });
  const [token, setToken] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [aiCredits, setAiCredits] = useState(0);
  const [teamCredits, setTeamCredits] = useState(0);
  const [isRefreshingCredits, setIsRefreshingCredits] = useState(false);
  const [lang, setLang] = useState("ar");
  const [activeTab, setActiveTab] = useState("dashboard");
  const [leafResult, setLeafResult] = useState(null);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false); // For Animation
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'light');
  const [userFarms, setUserFarms] = useState([]);
  const [userCrops, setUserCrops] = useState([]);
  const [userTrees, setUserTrees] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [userProfileData, setUserProfileData] = useState(null);

  // Chat Widget State for Guided Flows
  const [isSupportChatOpen, setIsSupportChatOpen] = useState(false);
  const [chatQuickReplies, setChatQuickReplies] = useState([]);

  // Registration Flow
  // Registration Flow
  const [isRegister, setIsRegister] = useState(false);
  const [isVerifyOTP, setIsVerifyOTP] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [regData, setRegData] = useState(null);

  // Google Onboarding Flow
  const [isGoogleOnboarding, setIsGoogleOnboarding] = useState(false);
  const [googleUserDetails, setGoogleUserDetails] = useState(null);

  const t = TranslationService.getTranslation(lang);
  const isRTL = lang === 'ar';
  const [isNavExpanded, setIsNavExpanded] = useState(false);

  useEffect(() => {
    if (activeTab === 'accounting') {
      setIsNavExpanded(false);
    }
  }, [activeTab]);
  // Custom Google Login Hook
  const loginWithGoogle = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setLoggingIn(true);
      try {
        const json = await ApiService.post('/google-login', { token: tokenResponse.access_token });
        if (json.status === "requires_onboarding") {
          setGoogleUserDetails(json);
          setIsGoogleOnboarding(true);
          setLoggingIn(false);
        } else {
          setToken(json.token);
          setCurrentUserId(json.user_id);
          if (json.ai_credits !== undefined) setAiCredits(json.ai_credits);
          if (json.team_credits !== undefined) setTeamCredits(json.team_credits);
          if (json.locale && json.locale.startsWith('ar')) setLang('ar');
          setLoggingIn(false);
        }
      } catch (err) {
        showToast("Google Login Error: " + err.message, "error");
        setLoggingIn(false);
      }
    },
    onError: () => {
      showToast('Google Login Failed', 'error');
    }
  });

  // Modal Toggle
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [isPaywallOpen, setIsPaywallOpen] = useState(false);
  const [isGuideOpen, setIsGuideOpen] = useState(false);

  useEffect(() => {
    const handleCreditsDepleted = () => setIsPaywallOpen(true);
    const handleRefreshCredits = async () => {
      if (!currentUserId) return;
      try {
        const json = await ApiService.get(`/me?user_id=${currentUserId}`);
        if (json.ai_credits !== undefined) setAiCredits(json.ai_credits);
        if (json.team_credits !== undefined) setTeamCredits(json.team_credits);
      } catch (e) { console.error("Credit fetch error", e); }
    };
    window.addEventListener('credits-depleted', handleCreditsDepleted);
    window.addEventListener('refresh-credits', handleRefreshCredits);
    return () => {
      window.removeEventListener('credits-depleted', handleCreditsDepleted);
      window.removeEventListener('refresh-credits', handleRefreshCredits);
    };
  }, [currentUserId]);

  // Manual AI Credits Refresh â€” called by the dedicated Refresh button in the header
  const handleManualCreditRefresh = async () => {
    if (!currentUserId || isRefreshingCredits) return;
    setIsRefreshingCredits(true);
    try {
      const json = await ApiService.get(`/me?user_id=${currentUserId}`);
      if (json.ai_credits !== undefined) setAiCredits(json.ai_credits);
      if (json.team_credits !== undefined) setTeamCredits(json.team_credits);
    } catch (e) {
      console.error('Credits refresh error:', e);
    } finally {
      setIsRefreshingCredits(false);
    }
  };

  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    // Keep the html background in sync to avoid canvas colour mismatch on resize
    document.documentElement.style.background = theme === 'dark' ? '#0A0F1E' : '#F4EFEA';
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
  }, [isRTL, lang]);

  // Fetch Dynamic Farm & Crop Hierarchy
  useEffect(() => {
    if (token && currentUserId) {
      const fetchGlobalData = async () => {
        try {
          const profile = await ApiService.get(`/profile/?user_id=${currentUserId}`);
          setUserProfileData(profile);

          const json = await ApiService.get(`/user-hierarchy/${currentUserId}`);
          if (json.farms) {
            setUserFarms(json.farms);
            setUserCrops(json.crops);
            setUserTrees(json.trees || []);
          }
        } catch (e) {
          console.error("Failed to fetch user global data", e);
        }
      };
      fetchGlobalData();
    }
  }, [token, currentUserId]);

  // Global Report Data State
  const [reportData, setReportData] = useState({
    // v1
    iot_data: {}, iot_ai_status: "Unknown", iot_ai_confidence: 0,
    leaf_diagnosis: {}, leaf_input_image_base64: null,
    soil_classification: {}, soil_input_image_base64: null,
    crop_inputs: {}, crop_recommendations: [],
    yield_inputs: {}, yield_prediction: {},
    satellite_data: {}, sat_date_current: "N/A", sat_date_past: "N/A", satellite_area: 0,
    ai_consultant_text: "",
    // v2
    iot_sensors_available: [], iot_device: {}, iot_rag_text: "",
    weather_data: {}, iot_history: {}, device_imagery: [],
    soil_ai_text: "", crop_ai_text: "", yield_ai_text: "",
    advanced_ai: {},
    accounting_summary: {}, accounting_revenue_breakdown: [], accounting_expense_breakdown: [],
    accounting_ai_text: "",
    control_automation: {}, control_ai_text: "",
    module_runs: {}
  });

  useEffect(() => {
    // Starts with the manifest name, so an installed app's window shows it once
    if (t && t.common) document.title = `GeoNutria AI | ${t.common.slogan}`;
  }, [t]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true); // Start Animation
    try {
      const json = await ApiService.post('/login', { username, password });
      setTimeout(() => { // Fake delay to show nice animation
        setToken(json.token);
        setCurrentUserId(json.user_id);
        if (json.ai_credits !== undefined) setAiCredits(json.ai_credits);
        if (json.team_credits !== undefined) setTeamCredits(json.team_credits);
        setLoggingIn(false);
      }, 1500);
    } catch (err) {
      if (err.message && err.message.includes("Account not verified")) {
        setRegData({ email: username });
        setIsVerifyOTP(true);
      } else {
        showToast("Incorrect Username or Password or Connection Error", "error");
      }
      setLoggingIn(false);
    }
  };

  const handleRegisterSubmit = async (payload) => {
    setLoggingIn(true);
    try {
      if (isGoogleOnboarding) {
        const reqPayload = { ...payload, google_token: googleUserDetails.google_token };
        const json = await ApiService.post('/google-register', reqPayload);
        setToken(json.token);
        setCurrentUserId(json.user_id);
        if (json.ai_credits !== undefined) setAiCredits(json.ai_credits);
        if (json.locale && json.locale.startsWith('ar')) setLang('ar');
        setIsGoogleOnboarding(false);
      } else {
        const reqPayload = { ...payload };
        const json = await ApiService.post('/register', reqPayload);
        showToast(json.message || "Registration Successful! Check your email.", "success");
        setRegData(payload);
        setIsVerifyOTP(true);
        setIsRegister(false);
      }
    } catch (err) {
      showToast("Registration Error: " + (err.message || "Email might exist"), "error");
    } finally {
      setLoggingIn(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const json = await ApiService.post('/verify-otp', { email: regData?.email, otp_code: otpCode });
      showToast(json.message || "Account verified! You can now log in.", "success");
      setIsVerifyOTP(false);
      setIsRegister(false);
    } catch (err) {
      showToast("Verification Error: " + (err.message || "Invalid OTP"), "error");
    } finally {
      setLoggingIn(false);
    }
  };

  if (!t) return (
    <div className="min-h-screen flex items-center justify-center bg-[#1B263B]">
      <div className="flex flex-col items-center gap-3">
        <span className="w-8 h-8 border-2 border-[#C47A2C]/40 border-t-[#C47A2C] rounded-full animate-spin" />
        <span className="text-white/60 text-sm">Loading GeoNutria...</span>
      </div>
    </div>
  );

  if (!token) {
    return (
      <LoginView
        toast={toast} setToast={setToast}
        theme={theme} setTheme={setTheme}
        lang={lang} setLang={setLang} isRTL={isRTL} t={t}
        loggingIn={loggingIn}
        username={username} setUsername={setUsername}
        password={password} setPassword={setPassword}
        handleLogin={handleLogin} loginWithGoogle={loginWithGoogle}
        isVerifyOTP={isVerifyOTP} otpCode={otpCode} setOtpCode={setOtpCode}
        handleVerifyOTP={handleVerifyOTP} regData={regData}
        isRegister={isRegister} setIsRegister={setIsRegister}
        isGoogleOnboarding={isGoogleOnboarding}
        googleUserDetails={googleUserDetails}
        handleRegisterSubmit={handleRegisterSubmit}
        setIsGoogleOnboarding={setIsGoogleOnboarding}
      />
    );
  }

  return (
    <div className={`flex h-screen relative w-full bg-[#F4EFEA] dark:bg-[#0A0F1E] transition-colors duration-300 font-sans antialiased text-[#1B263B] dark:text-[#F4EFEA]`} dir={isRTL ? 'rtl' : 'ltr'}>
      <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: '', type: 'success' })} />

      {/* ── FLOATING GLASSMORPHIC SIDEBAR ── */}
      <motion.aside
        key={lang}
        style={{ left: isRTL ? 'auto' : '1rem', right: isRTL ? '1rem' : 'auto' }}
        initial={{ width: 88, x: isRTL ? 20 : -20, opacity: 0 }}
        animate={{ width: isNavExpanded ? 260 : 88, x: 0, opacity: 1 }}
        onMouseEnter={() => setIsNavExpanded(true)}
        onMouseLeave={() => setIsNavExpanded(false)}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        className={`absolute top-4 bottom-4 z-50 flex flex-col rounded-[2rem] overflow-hidden
          bg-white/60 dark:bg-[#1B263B]/60 backdrop-blur-2xl
          border border-white/40 dark:border-white/10
          shadow-[0_8px_32px_rgba(27,38,59,0.1)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.3)]
          transition-colors duration-300`}
      >
        {/* Logo Area */}
        <div className="relative h-20 px-2 flex items-center justify-center border-b border-[#1B263B]/5 dark:border-white/5 bg-gradient-to-b from-white/20 to-transparent overflow-hidden">
          <motion.img
            animate={{ scale: isNavExpanded ? 1 : 0.8 }}
            src={GeoLogo}
            alt="GeoNutria"
            className="h-10 w-auto object-contain drop-shadow-lg"
          />
        </div>

        {/* Section label */}
        <motion.div
          animate={{ opacity: isNavExpanded ? 1 : 0, height: isNavExpanded ? 'auto' : 0 }}
          className="px-6 pt-5 pb-1 overflow-hidden whitespace-nowrap"
        >
          <span className="text-[0.62rem] font-bold tracking-[0.18em] text-[#1B263B]/40 dark:text-white/40 uppercase">
            {t.nav.navSection}
          </span>
        </motion.div>

        {/* Nav items (CSS ensures text hides smoothly when collapsed) */}
        {/* Nav items */}
        <nav className={`flex-1 flex flex-col transition-all duration-300
          ${isNavExpanded
            ? 'px-3 pt-0 pb-2 overflow-y-auto custom-scrollbar justify-evenly'
            : 'px-0 pt-6 pb-2 overflow-hidden items-center justify-evenly [&>*]:!w-[46px] [&>*]:!h-[46px] [&>*]:!p-0 [&>*]:!justify-center [&>*>*:not(svg)]:!hidden [&_svg]:!m-0 [&_svg]:!shrink-0'}
        `}>
          <NavBtn icon={LayoutDashboard} label={t.titles.dashboard} id="dashboard" active={activeTab} set={setActiveTab} isRTL={isRTL} />
          <NavBtn icon={Zap} label={t.control?.title || "Control"} id="control" active={activeTab} set={setActiveTab} isRTL={isRTL} />
          <NavBtn icon={Droplets} label={t.titles.irrigation} id="irrigation" active={activeTab} set={setActiveTab} isRTL={isRTL} />
          <NavBtn icon={Wheat} label={t.titles.crops} id="crops" active={activeTab} set={setActiveTab} isRTL={isRTL} />
          <NavBtn icon={TrendingUp} label={t.titles.yield} id="yield" active={activeTab} set={setActiveTab} isRTL={isRTL} />
          <NavBtn icon={Satellite} label={t.titles.satellite} id="satellite" active={activeTab} set={setActiveTab} isRTL={isRTL} />
          {/* <NavBtn icon={Bot} label={t.titles.consultant} id="consultant" active={activeTab} set={setActiveTab} isRTL={isRTL} /> */}
          <NavBtn icon={FlaskConical} label={t.titles.pesticide} id="pesticide" active={activeTab} set={setActiveTab} isRTL={isRTL} />
          <NavBtn icon={Sparkles} label={t.titles.advanced_ai} id="advanced_ai" active={activeTab} set={setActiveTab} isRTL={isRTL} />
          <NavBtn icon={Wallet} label={t.nav.billing} id="billing" active={activeTab} set={setActiveTab} isRTL={isRTL} />
          <NavBtn icon={Calculator} label={t.titles.accounting} id="accounting" active={activeTab} set={setActiveTab} isRTL={isRTL} />
          {username === 'ACFM_IT_Admin9573' && (
            <NavBtn icon={Shield} label={t.nav.admin} id="admin" active={activeTab} set={setActiveTab} isRTL={isRTL} />
          )}
        </nav>

        {/* Sidebar footer */}
        <div className="p-3 border-t border-[#1B263B]/5 dark:border-white/5 space-y-2 flex flex-col items-center">
          {/* Export button */}
          <motion.button
            whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
            onClick={() => setIsExportOpen(true)}
            className={`flex items-center justify-center gap-2 py-3 rounded-xl bg-[#1B263B]/5 dark:bg-white/5 hover:bg-[#C47A2C]/10 hover:text-[#C47A2C] transition-all font-medium overflow-hidden whitespace-nowrap ${isNavExpanded ? 'w-full px-4' : 'w-12 h-12 rounded-2xl'}`}
          >
            <FileText size={18} />
            {isNavExpanded && <span className="text-sm">{t.common.export}</span>}
          </motion.button>

          {/* Language & Logout */}
          <div className={`flex items-center w-full gap-2 ${isNavExpanded ? 'justify-between px-2 pt-2' : 'flex-col'}`}>
            <button
              onClick={() => setLang(lang === 'en' ? 'ar' : 'en')}
              title={lang === 'en' ? 'العربية' : 'English'}
              className="flex items-center gap-1.5 p-2 rounded-lg text-[#1B263B]/50 dark:text-white/40 hover:text-[#C47A2C] hover:bg-[#C47A2C]/10 transition-all"
            >
              <Languages size={18} />
              {isNavExpanded && <span className="text-xs font-bold">{lang === 'en' ? 'العربية' : 'English'}</span>}
            </button>

            <button
              onClick={() => { setToken(null); setCurrentUserId(null); }}
              title={t.common.logout}
              className="flex items-center gap-1.5 p-2 rounded-lg text-rose-400/80 hover:text-rose-500 hover:bg-rose-500/10 transition-all"
            >
              <LogOut size={18} className={isRTL ? "rotate-180" : ""} />
              {isNavExpanded && <span className="text-xs font-bold">{t.common.logout}</span>}
            </button>
          </div>
        </div>
      </motion.aside>
      {/* ── MAIN AREA ────────────────────────────────────── */}
      <main className={`flex-1 flex flex-col relative h-full transition-all duration-300 ltr:pl-[110px] rtl:pr-[110px]`}>
        {/* ── HEADER ─────────────────────────────────────── */}
        <header className="h-[62px] px-6 flex items-center justify-between
          bg-[#F4EFEA]/90 dark:bg-[#0A0F1E]/90
          backdrop-blur-xl
          sticky top-0 z-10
          border-b border-[#1B263B]/8 dark:border-white/5
          transition-colors duration-300"
        >
          {/* Farm / Plant selector */}
          <div className="flex items-center gap-4 flex-1 min-w-0">
            {/* Profile Avatar Trigger */}
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => setActiveTab('profile')}>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`w-10 h-10 rounded-full flex items-center justify-center border-2 border-[#C47A2C]/30 bg-[#1B263B]/5 dark:bg-white/5 text-[#1B263B] dark:text-white shadow-sm overflow-hidden ${activeTab === 'profile' ? 'ring-2 ring-[#C47A2C]' : ''}`}
              >
                {userProfileData?.picture ? (
                  <>
                    <img
                      src={userProfileData.picture.includes('googleusercontent.com') ? userProfileData.picture : `http://127.0.0.1:8009${userProfileData.picture}`}
                      alt="Profile"
                      onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
                      className="w-full h-full object-cover"
                    />
                    <User size={20} style={{ display: 'none' }} />
                  </>
                ) : (
                  <User size={20} />
                )}
              </motion.button>
              <span className="text-xs font-bold text-[#1B263B]/80 dark:text-white/80 uppercase tracking-wide whitespace-nowrap">{userProfileData?.name || t.common.yourProfile}</span>
            </div>

            <div className="w-px h-6 bg-[#1B263B]/10 dark:bg-white/10 mx-2"></div>

            <FarmPlantSelector
              t={t}
              farms={userFarms}
              crops={userCrops}
              trees={userTrees}
              onSelect={(k, v) => console.log('SelectedContext:', k, v)}
            />
          </div>

          {/* Right controls â€” ltr:ml-4 rtl:mr-4 keeps it at the far edge in both layouts */}
          <div className="flex items-center gap-3 ltr:ml-4 rtl:mr-4 flex-shrink-0">

            {/* Theme toggle */}
            <motion.button
              whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.92 }}
              onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
              className="p-2 rounded-full bg-[#1B263B]/5 dark:bg-white/8 hover:bg-[#1B263B]/10 dark:hover:bg-white/15 transition-all text-[#1B263B]/60 dark:text-white/60"
            >
              {theme === 'light' ? <Moon size={17} /> : <Sun size={17} />}
            </motion.button>

            {/* AI Credits chip */}
            <div className="flex items-center gap-1.5">
              {teamCredits > 0 && (
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-xl border transition-all bg-indigo-500/10 border-indigo-500/25 shadow-[0_0_10px_rgba(99,102,241,0.12)]`}>
                  <Users size={14} className="text-indigo-500 opacity-70" />
                  <span className="text-[0.68rem] font-semibold text-indigo-500/80 dark:text-indigo-400/80 uppercase tracking-wide">Team Credits</span>
                  <span className={`text-sm font-bold tabular-nums text-indigo-600 dark:text-indigo-400`}>
                    {teamCredits}
                  </span>
                </div>
              )}
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-xl border transition-all
                ${aiCredits > 0
                  ? 'bg-[#C47A2C]/8 border-[#C47A2C]/25 shadow-[0_0_10px_rgba(196,122,44,0.12)]'
                  : 'bg-rose-500/8 border-rose-500/25'
                }`}
              >
                <span className="text-[0.68rem] font-semibold text-[#1B263B]/50 dark:text-white/40 uppercase tracking-wide">{t.header.aiCredits}</span>
                <span className={`text-sm font-bold tabular-nums ${aiCredits > 0 ? 'text-[#C47A2C]' : 'text-rose-500'}`}>
                  {aiCredits}
                </span>
              </div>
              {/* Refresh button */}
              <motion.button
                id="credits-refresh-btn"
                whileTap={{ scale: 0.88 }}
                onClick={handleManualCreditRefresh}
                disabled={isRefreshingCredits || !currentUserId}
                title={t.header.refresh}
                className="p-1.5 rounded-lg hover:bg-[#C47A2C]/10 text-[#1B263B]/40 dark:text-white/30 hover:text-[#C47A2C] transition-all disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <RefreshCw
                  size={13}
                  className={isRefreshingCredits ? 'animate-spin text-[#6B8F71]' : 'transition-transform hover:rotate-180 duration-300'}
                />
              </motion.button>
            </div>

            {/* Top-Up button */}
            <motion.button
              whileHover={{ scale: 1.04, y: -1 }} whileTap={{ scale: 0.97 }}
              onClick={() => setActiveTab('billing')}
              className={`px-4 py-1.5 rounded-xl text-white text-xs font-semibold transition-all ${(aiCredits === 0 && teamCredits === 0)
                ? 'bg-gradient-to-r from-rose-500 to-rose-600 shadow-[0_4px_12px_rgba(239,68,68,0.35)]'
                : 'bg-gradient-to-r from-[#C47A2C] to-[#A36322] shadow-[0_4px_12px_rgba(196,122,44,0.35)]'
                }`}
            >
              {(aiCredits === 0 && teamCredits === 0) ? t.header.topUpEmpty : t.header.topUp}
            </motion.button>
          </div>
        </header>

        {/* â”€â”€ CONTENT AREA â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
        <div className="flex-1 overflow-auto p-4 md:p-6">
          <FeatureGuidelines />

          <div className={activeTab === 'dashboard' ? 'block min-h-full animate-fade-in-slide' : 'hidden'}><IoTView userId={currentUserId} t={t} isRTL={isRTL} lang={lang} setReportData={setReportData} theme={theme} selectedDevice={selectedDevice} setSelectedDevice={setSelectedDevice} setActiveTab={setActiveTab} setIsSupportChatOpen={setIsSupportChatOpen} setChatQuickReplies={setChatQuickReplies} /></div>
          <div className={activeTab === 'control' ? 'block animate-fade-in-slide' : 'hidden'}><ControlAutomationView userId={currentUserId} t={t} isRTL={isRTL} lang={lang} theme={theme} selectedDevice={selectedDevice} setReportData={setReportData} /></div>
          <div className={activeTab === 'irrigation' ? 'block animate-fade-in-slide' : 'hidden'}><WaterIrrigationView userId={currentUserId} lang={lang} theme={theme} /></div>
          <div className={activeTab === 'leaf' ? 'block animate-fade-in-slide' : 'hidden'}><LeafDoctorView t={t} userId={currentUserId || 1} onDiagnose={setLeafResult} setReportData={setReportData} theme={theme} /></div>
          <div className={activeTab === 'crops' ? 'block animate-fade-in-slide' : 'hidden'}><CropRecommendView t={t} userId={currentUserId || 1} isRTL={isRTL} setReportData={setReportData} selectedDevice={selectedDevice} /></div>
          <div className={activeTab === 'yield' ? 'block animate-fade-in-slide' : 'hidden'}><YieldPredictionView t={t} userId={currentUserId || 1} isRTL={isRTL} setReportData={setReportData} selectedDevice={selectedDevice} /></div>
          <div className={activeTab === 'satellite' ? 'block h-full animate-fade-in-slide' : 'hidden'}><SatelliteView t={t} userId={currentUserId || 1} isActive={activeTab === 'satellite'} setReportData={setReportData} selectedDevice={selectedDevice} /></div>
          <div className={activeTab === 'consultant' ? 'block animate-fade-in-slide' : 'hidden'}>
            <AIConsultantView t={t} isRTL={isRTL} reportData={reportData} lang={lang} setReportData={setReportData} />
          </div>
          <div className={activeTab === 'advanced_ai' ? 'block h-full animate-fade-in-slide' : 'hidden'}>
            <NewAIView theme={theme} isRTL={isRTL} lang={lang} setReportData={setReportData} userId={currentUserId || 1} />
          </div>
          <div className={activeTab === 'pesticide' ? 'block animate-fade-in-slide' : 'hidden'}>
            <PesticideRecommenderView t={t} isRTL={isRTL} lang={lang} />
          </div>
          <div className={activeTab === 'profile' ? 'block animate-fade-in-slide' : 'hidden'}>
            <ProfileView userId={currentUserId || 1} t={t} isRTL={isRTL} />
          </div>
          <div className={activeTab === 'billing' ? 'block animate-fade-in-slide' : 'hidden'}>
            <PaymentCheckout userId={currentUserId || 1} />
          </div>
          {username === 'ACFM_IT_Admin9573' && (
            <div className={activeTab === 'admin' ? 'block animate-fade-in-slide' : 'hidden'}>
              <AdminDashboardView adminId={currentUserId} t={t} isRTL={isRTL} />
            </div>
          )}
          <div className={activeTab === 'accounting' ? 'block h-full animate-fade-in-slide' : 'hidden'}>
            <AccountingView
              userId={currentUserId || 1}
              t={t}
              isRTL={isRTL}
              lang={lang}
              theme={theme}
              setReportData={setReportData}
              onClose={() => setActiveTab('dashboard')}
            />
          </div>
        </div>

        <ExportReportModal isOpen={isExportOpen} onClose={() => setIsExportOpen(false)} reportData={reportData} currentLang={lang} t={t} isRTL={isRTL} userFarms={userFarms} userId={currentUserId || 1} />
      </main>

      {/* Support Chat Widget */}
      <SupportChatWidget
        userId={currentUserId}
        isAdmin={username === 'ACFM_IT_Admin9573' || username === 'admin'}
        currentAdminId={currentUserId}
        isOpen={isSupportChatOpen}
        setIsOpen={setIsSupportChatOpen}
        quickReplies={chatQuickReplies}
        setQuickReplies={setChatQuickReplies}
        theme={theme}
        isRTL={isRTL}
      />
      <PaywallPopup
        isOpen={isPaywallOpen}
        onClose={() => setIsPaywallOpen(false)}
        onTopUp={() => { setIsPaywallOpen(false); setActiveTab('billing'); }}
      />

      {/* Floating Help Button — Stacked vertically above the chat FAB */}
      <motion.button
        whileHover={{ scale: 1.12 }} whileTap={{ scale: 0.93 }}
        onClick={() => setIsGuideOpen(true)}
        className={`fixed ${activeTab === 'advanced_ai' ? 'bottom-[11rem]' : 'bottom-[5.5rem]'} ltr:right-6 rtl:left-6 p-3.5 rounded-full shadow-xl z-40 flex items-center justify-center border group
          transition-all duration-300
          ${theme === 'dark'
            ? 'bg-[#1B263B] hover:bg-[#243450] text-white border-white/10 shadow-[0_4px_20px_rgba(0,0,0,0.4)]'
            : 'bg-white hover:bg-[#F4EFEA] text-[#1B263B] border-[#1B263B]/10 shadow-[0_4px_20px_rgba(27,38,59,0.15)]'
          }`}
        title="View Page Guide"
      >
        <HelpCircle size={24} className="drop-shadow-sm" />
        {/* Tooltip flips side in RTL */}
        <span className="absolute ltr:right-full ltr:mr-3 rtl:left-full rtl:ml-3 whitespace-nowrap bg-[#1B263B] dark:bg-white dark:text-[#1B263B] text-white font-medium text-xs px-3 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-lg">
          How to use this page
        </span>
      </motion.button>

      {/* Help Guide Modal */}
      <HelpGuideModal
        isOpen={isGuideOpen}
        onClose={() => setIsGuideOpen(false)}
        activeTab={activeTab}
        lang={lang}
        isRTL={isRTL}
      />
    </div>
  );
}
